<script lang="ts">
  import { type InputValue, type PortableTextComponents } from '@portabletext/svelte';
  import { PortableText } from '@portabletext/svelte';
  import { ComponentType } from '$lib/sanity/types';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';
  import Paragraph from '../PortableText/Paragraph.svelte';
  import FontMark from '../PortableText/Marks/FontMark.svelte';
  import TopBorderMark from '../PortableText/Marks/TopBorderMark.svelte';
  import Heading from '../PortableText/Heading.svelte';
  import Cookie from '../PortableText/Cookie.svelte';
  import Link from '../PortableText/Link.svelte';
  import Anchor from '../PortableText/Anchor.svelte';
  import List from '../PortableText/List/List.svelte';
  import ListItem from '../PortableText/List/ListItem.svelte';
  import TextDivider from '../PortableText/Custom/TextDivider.svelte';
  import RightAlign from './Marks/RightAlign.svelte';
  import CenterAlign from './Marks/CenterAlign.svelte';
  import Strong from './Marks/Strong.svelte';
  import Superscript from '../PortableText/Marks/Superscript.svelte';
  import Subscript from '../PortableText/Marks/Subscript.svelte';
  import { useThemeSpacerMultiplier } from '$components/Pages/PageThemeProvider.svelte';
  import GithubButtons from './Custom/GithubButtons.svelte';
  import Image from './Custom/Image.svelte';
  import ShapeLinkGroup from './Custom/PortableTextShapeLinkGroup.svelte';

  interface Props {
    body: InputValue;
    sourceMap?: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { body, sourceMap, path = [] }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(body, sourceMap, sanityStudioURL));

  let multiplier = $derived(useThemeSpacerMultiplier());

  let responsiveVars = $derived(
    new ResponsiveVars()
      .addFluid('wrapper-gap', toFluidVarInput(14 * multiplier.mobile, 18 * multiplier.desktop))
      .toCSS()
  );

  const components: Partial<PortableTextComponents> = {
    marks: {
      rightAlign: RightAlign,
      centerAlign: CenterAlign,
      link: Link,
      cookie: Cookie,
      anchor: Anchor,
      fontsPPNeueMachina: FontMark,
      topBorder: TopBorderMark,
      superScript: Superscript,
      subScript: Subscript,
      strong: Strong
    },
    block: {
      normal: Paragraph,
      dek: Paragraph,
      caption: Paragraph,
      breadcrumb: Paragraph,
      h1: Heading,
      h2: Heading,
      h3: Heading,
      h4: Heading,
      h5: Heading
    },
    list: {
      bullet: List,
      number: List
    },
    listItem: {
      normal: ListItem,
      dek: ListItem
    },
    types: {
      [ComponentType.Image]: Image,
      [ComponentType.ShapeLinkGroup]: ShapeLinkGroup,
      [ComponentType.TextDivider]: TextDivider,
      [ComponentType.GithubButtons]: GithubButtons
    }
  };
</script>

<div class={cx('wrapper', responsiveVars)} data-sanity={encodeDataAttribute([...path, 0, '_key'])}>
  <PortableText value={body} {components} />
</div>

<style>
  .wrapper {
    font-family: var(--font-family-primary);
    display: flex;
    flex-direction: column;
    gap: var(--wrapper-gap);
  }
</style>
